import React, { memo, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BASE as API_BASE, resolveMediaUrl } from '../../services/api';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import { toast } from '../../utils/toastBus';

function isPlaceholderHost(url) {
  try {
    const s = String(url || '');
    return /(^|\.)via\.placeholder\.com/i.test(s) || /(^|\.)placehold\.co/i.test(s);
  } catch { return false; }
}

function buildThumb(stream) {
  try {
    const preferred = String(stream?.thumbnailUrl || stream?.imageUrl || '');
    const raw = preferred;
    // If points to known placeholder host, prefer SVG and avoid network
    if (isPlaceholderHost(raw)) {
      const titlePh = String(stream?.title || 'Cover');
      const svgPh = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='1024' height='576'><rect width='100%' height='100%' fill='#111'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#ff7a45' font-family='Arial, Helvetica, sans-serif' font-size='32'>${titlePh.slice(0,60)}</text></svg>`)}\n`;
      return { base: '', bust: '', svg: svgPh };
    }

    const deriveBust = (url) => {
      if (!url) return '';
      const updatedAt = stream?.imageUpdatedAt || stream?.updatedAt || stream?.createdAt || null;
      if (!updatedAt) return url;
      const stamp = Number(new Date(updatedAt).getTime()) || Date.now();
      return `${url}${url.includes('?') ? '&' : '?'}cb=${stamp}`;
    };

    let base = '';
    let bust = '';
    if (raw.startsWith('/uploads/')) {
      base = `${API_BASE}${raw}`;
      bust = deriveBust(base);
    } else if (raw) {
      const proxied = `${API_BASE}/api/media/image/proxy?url=${encodeURIComponent(raw)}`;
      bust = deriveBust(proxied);
      base = raw; // direct fallback if proxy fails
    }

    const title = String(stream?.title || 'Cover');
    const svg = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='1024' height='576'><rect width='100%' height='100%' fill='#111'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#ff7a45' font-family='Arial, Helvetica, sans-serif' font-size='32'>${title.slice(0,60)}</text></svg>`)}\n`;
    return { base, bust: bust || base, svg };
  } catch {
    return { base: '', bust: '', svg: '' };
  }
}

const btn = {
  base: {
    background: '#374151', color: '#e5e7eb', border: 0, borderRadius: 8, padding: '6px 10px', cursor: 'pointer'
  },
  danger: {
    color: '#f87171', background: 'rgba(248, 113, 113, 0.1)', border: '1px solid rgba(248, 113, 113, 0.3)', borderRadius: 6, padding: '6px 10px', cursor: 'pointer'
  }
};

function StreamCard({
  stream,
  showActions = { like: true, play: true, share: true, delete: false, report: false },
  isLiked = false,
  onLike,
  onPlay,
  onShare,
  onDelete,
}) {
  const id = stream?._id || stream?.id;
  const likeCount = Number(stream?.likes ?? stream?.likeCount ?? stream?.likesCount ?? 0);
  const audioUrl = useMemo(() => {
    const raw = stream?.audioPath || stream?.audioUrl || '';
    return resolveMediaUrl(raw);
  }, [stream?.audioPath, stream?.audioUrl]);
  const videoUrl = useMemo(() => {
    const raw = stream?.videoUrl || stream?.videoPath || '';
    return resolveMediaUrl(raw);
  }, [stream?.videoUrl, stream?.videoPath]);
  const pageUrl = useMemo(() => {
    try {
      const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
      return (origin && id) ? `${origin}/streams/${encodeURIComponent(String(id))}` : '';
    } catch { return ''; }
  }, [id]);
  const shareUrl = useMemo(() => {
    try {
      // Prefer the public page URL for sharing; fall back to media URLs if needed
      return pageUrl || videoUrl || audioUrl || '';
    } catch { return pageUrl || videoUrl || audioUrl || ''; }
  }, [pageUrl, videoUrl, audioUrl]);

  const { play, pause, isPlaying } = useAudioPlayer();
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const [workingDelete, setWorkingDelete] = useState(false);
  const [workingLike, setWorkingLike] = useState(false);
  const [useVideo, setUseVideo] = useState(false);

  const { base, bust, svg } = buildThumb(stream || {});
  const imgSrc = bust || svg;

  return (
    <div style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 14 }}>
      <h4 style={{ color: '#e5e7eb', margin: '0 0 6px', fontSize: 16 }}>
        {id ? (
          <Link to={`/streams/${encodeURIComponent(String(id))}`} style={{ color: '#e5e7eb', textDecoration: 'underline' }}>
            {stream?.title || 'Untitled'}
          </Link>
        ) : (
          stream?.title || 'Untitled'
        )}
      </h4>

      <div style={{ marginBottom: 12 }}>
        {useVideo && videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            playsInline
            preload="metadata"
            autoPlay
            poster={imgSrc}
            style={{ width: '100%', borderRadius: 8, objectFit: 'cover', maxHeight: 220, background: '#000' }}
            onError={() => { try { setUseVideo(false); } catch {} }}
          />
        ) : (
          <img
            src={imgSrc}
            alt="Cover"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
            style={{ width: '100%', borderRadius: 8, objectFit: 'cover', maxHeight: 220 }}
            onError={(e) => {
              try {
                const el = e?.currentTarget;
                if (!el) return;
                if (base && el.dataset.triedBase !== '1') { el.dataset.triedBase = '1'; el.src = base; return; }
                el.src = svg;
              } catch {}
            }}
          />
        )}
      </div>

      {audioUrl || videoUrl ? (
        <>
          {!useVideo && audioUrl && (
            <audio
              ref={audioRef}
              src={audioUrl}
              style={{ width: '100%', marginBottom: 8 }}
              onPlay={() => play(id, audioRef.current)}
              onPause={() => pause(id)}
              controls
              preload="metadata"
            />
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {showActions.play && (
              <button
                aria-label={(useVideo ? (videoRef.current && !videoRef.current.paused) : isPlaying(id)) ? 'Pause' : 'Play'}
                onClick={async () => {
                  try {
                    if (videoUrl) {
                      // Prefer video if available
                      if (!useVideo) {
                        setUseVideo(true);
                        // allow next tick to mount video, then play
                        setTimeout(async () => { try { if (videoRef.current) await videoRef.current.play().catch(() => {}); } catch {} }, 0);
                      } else if (videoRef.current) {
                        if (videoRef.current.paused) {
                          await videoRef.current.play().catch(() => {});
                        } else {
                          videoRef.current.pause();
                        }
                      }
                    } else {
                      // Fallback to audio
                      const el = audioRef.current;
                      if (!el) return;
                      if (isPlaying(id)) {
                        pause(id);
                        el.pause();
                      } else {
                        await play(id, el);
                      }
                    }
                    onPlay && onPlay(id);
                  } catch {}
                }}
                style={{ ...btn.base, background: '#ff5a1f', color: '#fff' }}
              >
                {(useVideo ? (videoRef.current && !videoRef.current.paused) : isPlaying(id)) ? 'Pause' : 'Play'}
              </button>
            )}
            {showActions.share && (
              <button
                aria-label="Share"
                onClick={async () => {
                  try {
                    if (navigator.share) {
                      await navigator.share({ title: stream?.title || 'Stream', url: shareUrl });
                      toast({ type: 'success', message: 'Shared!' });
                    } else {
                      await navigator.clipboard.writeText(shareUrl);
                      toast({ type: 'success', message: 'Link copied' });
                    }
                    onShare && onShare(id);
                  } catch { toast({ type: 'error', message: 'Failed to share' }); }
                }}
                style={{ ...btn.base, padding: '6px 10px' }}
              >
                Share
              </button>
            )}
            {showActions.like && (
              <button
                aria-label="Like"
                disabled={workingLike}
                onClick={async () => {
                  if (!onLike) return;
                  setWorkingLike(true);
                  try { await onLike(id); } finally { setWorkingLike(false); }
                }}
                style={{ ...btn.base, padding: '6px 10px', background: isLiked ? '#ef4444' : btn.base.background }}
              >
                {isLiked ? '♥ Unlike' : '♡ Like'} ({likeCount})
              </button>
            )}
            {showActions.delete && (
              <button
                aria-label="Delete"
                disabled={workingDelete}
                onClick={async () => {
                  try {
                    if (!window.confirm('Delete this stream?')) return;
                    setWorkingDelete(true);
                    await onDelete?.(id);
                  } finally {
                    setWorkingDelete(false);
                  }
                }}
                style={btn.danger}
              >
                🗑️ Delete Stream
              </button>
            )}
          </div>
        </>
      ) : (
        <div style={{ color: '#9ca3af' }}>No audio</div>
      )}
    </div>
  );
}

export default memo(StreamCard);
